import React from 'react';
import I18n from '@/modules/i18n';
import { useModalManager } from '@/components/common/Modal';
import AcademicTermsModal from '@/components/admin/ProgramMappingsTable/AcademicTermsModal';
import Text from '@/components/ui/Text';
import SettingsToggle from './SettingsToggle';

type ProgramAccessKey = 'only_provision_current_terms' | 'student_seats_locked' | 'teacher_seats_locked';

interface ProgramAccessSettingsProps {
  autoRostered: boolean;
  disabled: boolean;
  onToggle: (key: ProgramAccessKey, value: boolean) => void;
  settings: Record<ProgramAccessKey, boolean>;
  subscriberId: number;
}

const SETTINGS: { descriptionKey: string; key: ProgramAccessKey; labelKey: string }[] = [
  {
    descriptionKey: 'manage_teacher_program_access_description',
    key: 'teacher_seats_locked',
    labelKey: 'manage_teacher_program_access',
  },
  {
    descriptionKey: 'manage_student_program_access_description',
    key: 'student_seats_locked',
    labelKey: 'manage_student_program_access',
  },
  {
    descriptionKey: 'only_include_active_terms_description',
    key: 'only_provision_current_terms',
    labelKey: 'only_include_active_terms',
  },
];

const ProgramAccessSettings = ({
  autoRostered,
  disabled,
  onToggle,
  settings,
  subscriberId,
}: ProgramAccessSettingsProps) => {
  const academicTermsModalManager = useModalManager();

  return (
    <div>
      <Text className="tw-mb-2" size="xl" tag="h2" variant="label">
        {I18n.t('program_access')}
      </Text>
      <div className="tw-space-y-2">
        {SETTINGS.map((setting) => {
          const lockedByRostering = autoRostered && setting.key === 'student_seats_locked'
            && settings.student_seats_locked;
          const description = lockedByRostering
            ? `${I18n.t(setting.descriptionKey)} ${I18n.t('student_program_access_managed_by_rostering')}`
            : I18n.t(setting.descriptionKey);
          const lockReason = lockedByRostering
            ? 'student_program_access_managed_by_rostering'
            : 'only_district_admins_can_modify';

          return (
            <SettingsToggle
              key={setting.key}
              checked={settings[setting.key]}
              description={description}
              disabled={disabled || lockedByRostering}
              disabledTooltip={I18n.t(lockReason)}
              id={`admin-setting-${setting.key}`}
              label={I18n.t(setting.labelKey)}
              onChange={(checked: boolean) => onToggle(setting.key, checked)}
            />
          );
        })}
        <button
          className="btn btn--outline-purple"
          type="button"
          onClick={() => academicTermsModalManager.open()}
        >
          {I18n.t('select_academic_terms')}
        </button>
      </div>

      <AcademicTermsModal
        modalManager={academicTermsModalManager}
        subscriberId={String(subscriberId)}
      />
    </div>
  );
};

export default ProgramAccessSettings;
